import * as THREE from "three/webgpu";
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import type {SketchDrawingManager} from "../manager/SketchDrawingManager";
import {SketchDrawingTool} from './DrawingSharedTypes';


/** 多段线预览颜色。 */
const POLYLINE_PREVIEW_COLOR = 0x53a9ff;


/**
 * 草图多段线预览处理器。
 */
export class SketchPolylineDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Polyline;
    /**
     * 是否正在完成多段线绘制。
     *
     * 说明：双击闭合、端点闭合和 Enter 可能在相邻事件中重复触发，必须用处理器级门闩
     * 保证同一批 drawingPoints 只进入一个运行态历史会话。
     */
    private isFinishingPolylineDrawing: boolean = false;

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    
    getRequiredPointCount(): number {
        return Infinity;
    }

    
    get closable(): boolean {
        return true;
    }

    
    getDrawingHint(): string {
        const pointCount = this.drawingPoints.length;
        const extraInfo = pointCount > 0 ? ` (${pointCount}个点)` : '';
        const actionHint = pointCount >= 2
            ? '双击/Enter完成 | Backspace撤销点 | ESC取消'
            : 'ESC取消';
        return `依次点击多个点${extraInfo} | ${actionHint}`;
    }

    
    resetState(): void {
        this.drawingPoints = [];
    }

    /**
     * 处理多段线绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const resolved = this.resolveMultiPointDrawingPoint(payload);
        if (!resolved) {
            console.log('无法获取多段线绘制位置');
            return;
        }
        const finished = this.handleMultiPointCommit(resolved, false);
        if (finished) {
            return;
        }
        this.drawingPoints.push(resolved.clone());
    }


    /**
     * 更新多段线预览。

     * @param currentPoint 当前预览点。
     */
    updatePolylinePreviewByHandler(currentPoint: THREE.Vector3): void {

        this.createPolylinePreviewInHandler(currentPoint);
    }


    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        this.updatePolylinePreviewByHandler(currentPoint);
    }

    /**
     * 供管理器调用的多段线完成入口。
     */
    public finalizePolylineByHandler(options?: { stopDrawing?: boolean }): Promise<void> {
        return this.finishPolylineDrawingByHandler(options);
    }

    /**
     * 在处理器内创建多段线预览。

     * @param currentPoint 当前预览点。
     */
    private createPolylinePreviewInHandler(currentPoint: THREE.Vector3): void {
        this.destroyPreview();
        const points = [...this.drawingPoints, currentPoint.clone()];
        if (points.length < 2) {
            return;
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const isConstruction = this.sketchDrawingManager.sketchLineDrawingHandler.lineOptions.forConstruction ?? false;
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: POLYLINE_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: POLYLINE_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
        this.sketchDrawingManager.previewLine = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            this.sketchDrawingManager.previewLine.computeLineDistances();
        }
        this.viewer.addObject(this.sketchDrawingManager.previewLine);
    }

    /**
     * 解析多点工具绘制点。
     */
    private resolveMultiPointDrawingPoint(payload: SketchDrawingMouseDownPayload): THREE.Vector3 {
        return payload.snappedPoint ?? this.getDrawingPoint(payload.event);
    }

    /**
     * 处理多点工具提交与闭合。
     */
    private handleMultiPointCommit(point: THREE.Vector3, updatePreview: boolean): boolean {
        const chainStartPoint = this.resolveChainStartPointForClosure();
        const segmentStartPoint = this.resolveSegmentStartPointForClosure();
        const closureViaEndpoint = Boolean(
            segmentStartPoint
        );

        if (this.drawingPoints.length >= 2 && chainStartPoint && this.isWithinClosureTolerance(point, chainStartPoint)) {
            this.drawingPoints.push(chainStartPoint.clone());
            this.scheduleFinishPolylineDrawing();
            return true;
        }
        if (this.drawingPoints.length >= 2 && closureViaEndpoint) {
            this.drawingPoints.push(point.clone());
            this.scheduleFinishPolylineDrawing();
            return true;
        }
        const currentTime = Date.now();
        const timeSinceLastClick = currentTime - this.sketchDrawingManager.getLastLineClickTimestamp();
        if (timeSinceLastClick < this.sketchDrawingManager.getLineDoubleClickThreshold() && this.drawingPoints.length >= 2) {
            this.scheduleFinishPolylineDrawing();
            return true;
        }
        this.sketchDrawingManager.setLastLineClickTimestamp(currentTime);
        void updatePreview;
        return false;
    }

    /**
     * 在处理器内完成多段线绘制。

     */
    private async finishPolylineDrawingByHandler(options?: { stopDrawing?: boolean }): Promise<void> {
        if (this.isFinishingPolylineDrawing) {
            return;
        }
        if (this.drawingPoints.length < 2 ) {
            return;
        }
        this.isFinishingPolylineDrawing = true;
        try {
            await this.sketchDrawingManager.executeActiveSketchRuntimeHistorySession({
                label: `草图绘制：${SketchDrawingTool.Polyline}`,
                commandType: `sketch.runtime.draw.${SketchDrawingTool.Polyline}`,
                work: async () => {
                    this.finishPolylineDrawingCore(options);
                },
            });
        } finally {
            this.isFinishingPolylineDrawing = false;
        }
    }

    /**
     * 调度多段线完成提交并集中处理异步失败。
     */
    private scheduleFinishPolylineDrawing(): void {
        void this.finishPolylineDrawingByHandler().catch((error) => {
            console.error('多段线绘制提交失败', error);
            this.viewer.drawingNoticeCallback?.('多段线绘制提交失败，请重试。', 'error');
        });
    }

    /**
     * 执行多段线实体创建与绘制状态清理。
     *
     * 说明：该方法只包含实际变更，事务捕获由 finishPolylineDrawingByHandler 统一负责，
     * 避免不定点工具在每次点击时克隆整组草图导致交互卡死。
     */
    private finishPolylineDrawingCore(options?: { stopDrawing?: boolean }): void {
        if (this.drawingPoints.length < 2 ) {
            return;
        }
        this.createPolylineEntityByHandler();
        this.destroyPreview();
        if (options?.stopDrawing) {
            this.sketchDrawingManager.stopDrawing();
            return;
        }
        this.drawingPoints = [];
        this.sketchDrawingManager.setPreviewPoint(null);
        this.sketchDrawingManager.setLastLineClickTimestamp(0);
    }

    /**
     * 在处理器内创建多段线实体。
     */
    private createPolylineEntityByHandler(): void {
        if (this.drawingPoints.length < 2 ) {
            return;
        }
        const points = [...this.drawingPoints];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = this.createSketchLineMaterial(false, false);
        const line = new THREE.Line(geometry, material);

        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);

        this.viewer.addSketchObject(line);
        this.sketchDrawingManager.notifySketchEntityCreated(line);
    }

}

